import { useWrappersFetcher } from "./connector";
import { components } from "./spec";

// Event authentication and details
export const sitchEventAuthForEvent = async (
  request: components["schemas"]["AuthForEventRequest"],
): Promise<components["schemas"]["SuccessResponse"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/auth-for-event/")
    .method("post")
    .create()(request);
  return response.data;
};

export const sitchEventCheckAuth = async (
  request: components["schemas"]["CheckAuthRequest"],
): Promise<components["schemas"]["SuccessResponse"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/check-auth/")
    .method("post")
    .create()(request);
  return response.data;
};

export const sitchEventDetails = async (
  event: string,
): Promise<components["schemas"]["SitchEventDetailSchema"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/event-details/")
    .method("get")
    .create()({ event });
  return response.data;
};

// Attendees
export const sitchEventListAttendees = async (
  event: string,
  searchTerm?: string,
): Promise<components["schemas"]["ListEventAttendeesResponse"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/list-event-attendees/")
    .method("get")
    .create()({ event, search_term: searchTerm });
  return response.data;
};

export const sendEventTextBlast = async (
  request: components["schemas"]["SendTextBlastRequest"],
): Promise<components["schemas"]["SuccessResponse"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/send-text-blast/")
    .method("post")
    .create()(request);
  return response.data;
};

export const sitchEventAddGuest = async (
  request: components["schemas"]["EventAddGuestRequest"],
): Promise<components["schemas"]["SuccessResponse"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/add-event-guest/")
    .method("post")
    .create()(request);
  return response.data;
};

export const sitchEventAttendeesSummary = async (
  event: string,
): Promise<components["schemas"]["EventAttendeesSummaryResponse"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/event-attendees-summary/")
    .method("get")
    .create()({ event });
  return response.data;
};

export const sitchEventRemoveAttendee = async (
  eventId: string,
  memberId: string,
): Promise<components["schemas"]["RemoveMemberFromEventResponse"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/remove-member-from-event/")
    .method("post")
    .create()({ event: eventId, member: memberId });
  return response.data;
};

// Round management
export const sitchEventRegenerateRound = async (
  request: components["schemas"]["RegenerateRoundRequest"],
): Promise<components["schemas"]["SuccessWithTaskSchema"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/regenerate-round/")
    .method("post")
    .create()(request);
  return response.data;
};

export const sitchEventAcceptRound = async (
  request: components["schemas"]["AcceptRoundRequest"],
): Promise<components["schemas"]["SuccessWithTaskSchema"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/accept-round/")
    .method("post")
    .create()(request);
  return response.data;
};

// Candidates management
export const sitchEventListCandidatesForRound = async (
  eventRound: string,
  manualOnly?: boolean,
): Promise<components["schemas"]["EventCandidateSummarySchema"][]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/list-candidates-for-round/")
    .method("get")
    .create()({ event_round: eventRound, manual_only: manualOnly });
  return response.data;
};

export const sitchEventListRoundCandidatesForMember = async (
  round: string,
  eventMember: string,
): Promise<components["schemas"]["PotentialEventCandidate"][]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/list-round-candidates-for-member/")
    .method("get")
    .create()({ round, event_member: eventMember });
  return response.data;
};

export const sitchEventAddManualSetupToRound = async (
  request: components["schemas"]["AddManualSetupToRoundRequest"],
): Promise<components["schemas"]["AddManualSetupToRoundResponse"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/add-manual-setup-to-round/")
    .method("post")
    .create()(request);
  return response.data;
};

export const sitchEventRejectCandidateFromRound = async (
  request: components["schemas"]["RejectCandidateFromRoundRequest"],
): Promise<components["schemas"]["SuccessResponse"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/reject-candidate-from-round/")
    .method("post")
    .create()(request);
  return response.data;
};

export const sitchEventRemoveManualSetupFromRound = async (
  request: components["schemas"]["RemoveManualSetupFromRoundRequest"],
): Promise<components["schemas"]["SuccessResponse"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/remove-manual-setup-from-round/")
    .method("post")
    .create()(request);
  return response.data;
};

export const sitchEventCreateManualSetupForRound = async (
  request: components["schemas"]["AddManualSetupToRoundRequest"],
): Promise<components["schemas"]["AddManualSetupToRoundResponse"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/add-manual-setup-to-round/")
    .method("post")
    .create()(request);
  return response.data;
};

// Task status and utilities
export const sitchEventTaskStatus = async (
  taskId: string,
): Promise<components["schemas"]["TaskStatusResponse"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/task-status/")
    .method("get")
    .create()({ task_id: taskId });
  return response.data;
};

export const sitchEventCheckForNewUsersForRound = async (
  round: string,
): Promise<components["schemas"]["NewUsersAvailableForRoundResponse"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/check-for-new-users-for-round/")
    .method("get")
    .create()({ round });
  return response.data;
};

export const sitchEventSendReminder = async (
  request: components["schemas"]["SendEventReminderRequest"],
): Promise<components["schemas"]["ReminderInfo"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/send-event-reminder/")
    .method("post")
    .create()(request);
  return response.data;
};

export const sitchEventGetReminder = async (
  event: string,
): Promise<components["schemas"]["ReminderInfo"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/get-event-reminder-status/")
    .method("get")
    .create()({ event });
  return response.data;
};

export const sitchEventStartNewRound = async (
  request: components["schemas"]["StartNewRoundRequest"],
): Promise<components["schemas"]["SuccessWithTaskSchema"]> => {
  const response = await useWrappersFetcher()
    .path("/api/v1/sitch-events/start-new-round/")
    .method("post")
    .create()(request);
  return response.data;
};
